// The ProductType enum is essentially two distinct enums that overlap:
// - BaseProductType: Represents product types used in backend responses for products, drive, etc.
//   There is exception from this - constants api, which
// - AppContextProductType: Similar to ProductType, but excludes ProductType.Diamond.
// In the new code types, one of these enums should be used instead of ProductType.
// Using ProductType.Diamond in the app context will result in an error.
// Similarly, using ProductType.DiamondColorless where BaseProductType (e.g., ProductType.Diamond) is expected will also cause an error.

export type BaseProductType = ProductType.Diamond | ProductType.Rough | ProductType.Jewelry | ProductType.Gem;
export type AppContextProductType = Exclude<ProductType, ProductType.Diamond>;

export enum ProductType {
  Diamond = 'diamond',
  DiamondColorless = 'diamond-colorless',
  DiamondFancy = 'diamond-fancy',
  LGDColorless = 'lgd-colorless',
  LGDFancy = 'lgd-fancy',
  Rough = 'rough',
  LGDRough = 'lgd-rough',
  Jewelry = 'jewelry',
  Gem = 'gem',
}

export const TYPE_DIAMOND = ProductType.Diamond;
export const TYPE_DIAMOND_COLORLESS = ProductType.DiamondColorless;
export const TYPE_DIAMOND_FANCY = ProductType.DiamondFancy;
export const TYPE_LGD_COLORLESS = ProductType.LGDColorless;
export const TYPE_LGD_FANCY = ProductType.LGDFancy;
export const TYPE_ROUGH = ProductType.Rough;
export const TYPE_LGD_ROUGH = ProductType.LGDRough;
export const TYPE_JEWELRY = ProductType.Jewelry;
export const TYPE_GEM = ProductType.Gem;

export const BASE_PRODUCT_TYPE_ORDER: BaseProductType[] = [
  ProductType.Diamond,
  ProductType.Rough,
  ProductType.Jewelry,
  ProductType.Gem,
];

export const COMPARE_DIAMOND_GROUP = new Set([
  ProductType.DiamondColorless,
  ProductType.DiamondFancy,
  ProductType.LGDColorless,
  ProductType.LGDFancy,
]);

export const COMPARE_ROUGH_GROUP = new Set([ProductType.Rough, ProductType.LGDRough]);

export const ALL_PRODUCT_TYPES = [
  ProductType.Diamond,
  ProductType.DiamondColorless,
  ProductType.DiamondFancy,
  ProductType.LGDColorless,
  ProductType.LGDFancy,
  ProductType.LGDRough,
  ProductType.Jewelry,
  ProductType.Rough,
  ProductType.Gem,
];

export const COMPARE_WITH_REFERENCE_PRODUCT_TYPES = [ProductType.DiamondColorless, ProductType.LGDColorless] as const;

export type CompareWithReferenceProductType = (typeof COMPARE_WITH_REFERENCE_PRODUCT_TYPES)[number];

export const APP_CONTEXT_PRODUCT_TYPES: AppContextProductType[] = [
  ProductType.DiamondColorless,
  ProductType.DiamondFancy,
  ProductType.LGDColorless,
  ProductType.LGDFancy,
  ProductType.Rough,
  ProductType.LGDRough,
  ProductType.Jewelry,
  ProductType.Gem,
];

export function reverseProductType(productType: ProductType) {
  switch (productType) {
    case ProductType.DiamondColorless:
      return ProductType.LGDColorless;
    case ProductType.DiamondFancy:
      return ProductType.LGDFancy;
    case ProductType.Rough:
      return ProductType.LGDRough;
    case ProductType.LGDColorless:
      return ProductType.DiamondColorless;
    case ProductType.LGDFancy:
      return ProductType.DiamondFancy;
    case ProductType.LGDRough:
      return ProductType.Rough;
    default:
      return productType;
  }
}

export function isFancyDiamond(productType: ProductType) {
  return productType === ProductType.DiamondFancy || productType === ProductType.LGDFancy;
}

export function isColorlessDiamond(productType: ProductType) {
  return productType === ProductType.DiamondColorless || productType === ProductType.LGDColorless;
}

export function isDiamond(productType: ProductType) {
  return productType === ProductType.Diamond || isFancyDiamond(productType) || isColorlessDiamond(productType);
}

export function getIsLabGrown(productType: Nullable<ProductType>): Nullable<boolean> {
  switch (productType) {
    case ProductType.LGDColorless:
    case ProductType.LGDFancy:
      return true;
    case ProductType.Diamond:
    case ProductType.DiamondColorless:
    case ProductType.DiamondFancy:
      return false;
    default:
      return null;
  }
}

export function isRough(productType: ProductType) {
  return productType === ProductType.Rough || productType === ProductType.LGDRough;
}

export function baseTypeToAppContextProductType(
  baseType: BaseProductType,
  isLabGrown: boolean = false,
  isFancy: boolean = false,
): AppContextProductType {
  if (!isLabGrown) {
    if (baseType === ProductType.Diamond) {
      return isFancy ? ProductType.DiamondFancy : ProductType.DiamondColorless;
    }

    return baseType;
  }

  switch (baseType) {
    case ProductType.Diamond:
      return isFancy ? ProductType.LGDFancy : ProductType.LGDColorless;
    case ProductType.Rough:
      return ProductType.LGDRough;
    case ProductType.Jewelry:
      return ProductType.Jewelry;
    case ProductType.Gem:
      return ProductType.Gem;
  }
}

export function productTypeToBaseProductType(productType: ProductType): BaseProductType {
  if (isRough(productType)) {
    return ProductType.Rough;
  } else if (productType === ProductType.Jewelry) {
    return ProductType.Jewelry;
  } else if (productType === ProductType.Gem) {
    return ProductType.Gem;
  } else {
    return ProductType.Diamond;
  }
}

export function formatBaseProductTypeLabel(count: number, productType: BaseProductType): string {
  if (productType === ProductType.Rough) {
    return count === 1 ? 'rough diamond' : 'rough diamonds';
  }

  if (count === 1) {
    return productType;
  }

  if (isDiamond(productType)) {
    return 'diamonds';
  }

  if (productType === ProductType.Gem) {
    return 'gems';
  }

  return productType;
}
